import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Home.css';
import { voteStore } from '../utils/voteStore';

const Home = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeNewsletter, setAgreeNewsletter] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Clear any existing vote data when returning to home (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      voteStore.clear();
    }
  }, []);

  const isValidEmail = useMemo(() => {
    if (!email) return false;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }, [email]);

  const canStartVoting = useMemo(() => {
    return fullName.trim().length > 0 && isValidEmail && agreeTerms;
  }, [fullName, isValidEmail, agreeTerms]);

  const hasEmailAlreadyVoted = (responseBody) => {
    if (!responseBody || typeof responseBody !== 'object') {
      return false;
    }

    const directBooleanKeys = ['isVoted', 'alreadyVoted', 'hasVoted', 'voted', 'exists'];
    for (const key of directBooleanKeys) {
      if (typeof responseBody[key] === 'boolean') {
        return responseBody[key];
      }
    }

    if (typeof responseBody.status === 'string') {
      const normalizedStatus = responseBody.status.trim().toLowerCase();
      return normalizedStatus === 'voted' || normalizedStatus === 'already_voted' || normalizedStatus === 'already-voted';
    }

    return false;
  };

  const handleStart = async (e) => {
    e.preventDefault();
    if (!canStartVoting || isCheckingEmail) return;

    const normalizedEmail = email.trim();
    const normalizedFullName = fullName.trim();

    try {
      setIsCheckingEmail(true);

      const response = await fetch('https://mca.hot-mob.com/api/voting/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      if (!response.ok) {
        throw new Error(`Failed to validate email (${response.status})`);
      }

      const responseBody = await response.json().catch(() => ({}));
      if (hasEmailAlreadyVoted(responseBody)) {
        window.alert('此電郵已完成投票，不能重複投票。');
        return;
      }

      voteStore.saveUser({
        fullName: normalizedFullName,
        email: normalizedEmail,
        agreeTerms,
        agreeNewsletter,
        ts: Date.now(),
      });
      navigate('/voting/site');
    } catch (err) {
      window.alert(err?.message || 'Failed to validate email, please try again.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const isClient = typeof window !== 'undefined';
  
  const content = (
    <div className="form-card">
            {/* <h1 className="form-title">參與投票 Participation</h1>
            <p className="form-subtitle">請先填寫基本資料並閱讀《條款及細則》</p> */}

            <form className="vote-form" onSubmit={handleStart}>
              <div className="form-group">
                <div className="form-input-group form-input-group-name">
                  <label className="form-label" htmlFor="fullName">姓名 Name</label>
                  <input
                    id="fullName"
                    type="text"
                    className="form-input"
                    placeholder="請輸入您的姓名"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-input-group">
                  <label className="form-label" htmlFor="email">電郵 Email</label>
                  <input
                    id="email"
                    type="email"
                    className={`form-input${email && !isValidEmail ? ' error' : ''}`}
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <div className="form-hint">每個電郵地址只可登記一次</div>
                  {email && !isValidEmail && (
                    <div className="error-message">請輸入有效的電郵地址</div>
                  )}
                </div>
              </div>

              <div className="tc-box">
                <h3 className="tc-title">《條款及細則》</h3>
                <ol className="tc-list">
                  <li>
                    投票活動由2026年9月1日 （中午12時正）至9月30日（晚上11時59分）止，
                    主辦單位具備更改活動日期及時間之最終決定權而毋須另行通知。
                  </li>
                  <li>
                    如對是次活動有任何異議或爭議，主辦單位保留最終決定權，毋須作另行通知。
                  </li>
                  <li>
                    如有任何查詢，請電郵至 <a href="mailto:info@hkaim.org">info@hkaim.org</a>
                  </li>
                  <li>
                    如欲了解上屆傳媒轉型大獎，可前往活動專頁(<a href="https://mca.hkaim.org/#home" target="_blank" rel="noreferrer">https://mca.hkaim.org/#home</a>)。
                  </li>
                </ol>
              </div>

              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                  />
                  <span>我已閱讀及明白有關《條款及細則》並同意接受其約束。</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={agreeNewsletter}
                    onChange={(e) => setAgreeNewsletter(e.target.checked)}
                  />
                  <span>我同意加入香港互動市務商會的通訊名單，接收活動資訊和電子通訊。</span>
                </label>
              </div>

              <div className="form-actions">
                <button
                  id="start-voting-button"
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={!canStartVoting || isCheckingEmail}
                  aria-disabled={!canStartVoting || isCheckingEmail}
                >
                  {isCheckingEmail ? '檢查中...' : '開始投票'}
                </button>
              </div>
            </form>
    </div>
  );

  return (
    <div className="page-container">
      <div className="container">
        {isClient ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {content}
          </motion.div>
        ) : (
          content
        )}
      </div>
    </div>
  );
};

export default Home;
